// Owner: Anfragen aus dem Kontaktformular und „Verträge hier kündigen".
//
// Eine Kündigung aus dem Formular wird hier dem Betrieb zugeordnet und
// ausgeführt — sie gilt zum Termin, der sich aus dem Eingang ergibt. Die
// Bestätigung mit Vertragsende geht dabei automatisch an den Betrieb.
import { useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten } from '../../../gemeinsam/lib/daten';
import { Abzeichen, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { meldungZeigen } from '../../../gemeinsam/komponenten/Meldungen';
import type { AboStand } from '../../../gemeinsam/lib/typen';

type Anfrage = {
  id: string; grund: string; name: string; email: string; firma: string; kennung: string;
  rechnungsnummer: string; nachricht: string; eingangAm: string; status: 'offen' | 'erledigt';
  betriebe: Array<{ id: string; name: string; abo: AboStand }>;
};

const GRUND: Record<string, string> = {
  frage: 'Frage', support: 'Technische Hilfe', rueckerstattung: 'Rückerstattung',
  kuendigung: 'Kündigung', datenschutz: 'Datenschutz', sonstiges: 'Sonstiges',
};
const zeit = (s: string) => new Date(s).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' });

export default function Anfragen() {
  const { daten, laedt, fehler, neuLaden } = useDaten<Anfrage[]>('/billing/admin/anfragen');
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [aktionsFehler, setAktionsFehler] = useState<string | null>(null);

  async function tun(id: string, arbeit: () => Promise<string>) {
    setBeschaeftigt(id);
    setAktionsFehler(null);
    try { meldungZeigen(await arbeit()); await neuLaden(); }
    catch (e) { setAktionsFehler(e instanceof ApiFehler ? e.message : 'Fehlgeschlagen.'); }
    finally { setBeschaeftigt(null); }
  }

  if (laedt && !daten) return <div className="flex justify-center py-16"><Spinner /></div>;
  const liste = daten ?? [];
  const offen = liste.filter(a => a.status === 'offen').length;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Anfragen</h1>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {aktionsFehler && <Hinweis art="fehler">{aktionsFehler}</Hinweis>}
      <Karte>
        <KartenKopf titel="Kontaktformular & Kündigungen" hinweis={`${offen} offen · ${liste.length} insgesamt`} />
        {liste.length === 0 ? <LeerZustand titel="Keine Anfragen" /> : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(a => (
              <li key={a.id} className={`space-y-2 px-5 py-4 ${a.status === 'erledigt' ? 'opacity-60' : ''}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Abzeichen art={a.grund === 'kuendigung' ? 'rot' : a.grund === 'rueckerstattung' ? 'gelb' : 'blau'}>{GRUND[a.grund] ?? a.grund}</Abzeichen>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{a.firma || a.name}</span>
                  <a href={`mailto:${a.email}`} className="text-sm text-marke-600 hover:underline dark:text-marke-400">{a.email}</a>
                  <span className="ms-auto text-xs text-slate-500 dark:text-slate-400">{zeit(a.eingangAm)}</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {a.name}{a.kennung && ` · Kennung ${a.kennung}`}{a.rechnungsnummer && ` · Rechnung ${a.rechnungsnummer}`}
                </p>
                {a.nachricht && <p className="whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-200">{a.nachricht}</p>}

                {a.grund === 'kuendigung' && a.status === 'offen' && (
                  a.betriebe.length === 0
                    ? <Hinweis art="warnung">Kein Betrieb mit dieser E-Mail-Adresse — bitte beim Absender nachfragen und unter „Mandanten" kündigen.</Hinweis>
                    : (
                      <div className="flex flex-wrap gap-2">
                        {a.betriebe.map(b => (
                          <Knopf key={b.id} variante="gefahr" className="!text-xs" laedt={beschaeftigt === a.id}
                                 disabled={b.abo.phase === 'gekuendigt' || b.abo.phase === 'beendet'}
                                 onClick={() => tun(a.id, async () => {
                                   await api(`/billing/admin/tenants/${b.id}/kuendigen`, { methode: 'POST', daten: { anfrageId: a.id } });
                                   return `${b.name}: gekündigt. Die Bestätigung mit Vertragsende ist an den Betrieb unterwegs.`;
                                 })}>
                            {b.abo.phase === 'gekuendigt' || b.abo.phase === 'beendet' ? `${b.name}: schon gekündigt` : `„${b.name}" kündigen`}
                          </Knopf>
                        ))}
                      </div>
                    )
                )}

                <Knopf variante="still" className="!text-xs" laedt={beschaeftigt === a.id}
                       onClick={() => tun(a.id, async () => {
                         await api(`/billing/admin/anfragen/${a.id}/erledigt`, { methode: 'POST', daten: { erledigt: a.status === 'offen' } });
                         return a.status === 'offen' ? 'Als erledigt markiert.' : 'Wieder offen.';
                       })}>
                  {a.status === 'offen' ? 'Erledigt' : 'Wieder öffnen'}
                </Knopf>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}
